import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import { UserPlus, Mail, Shield, MoreHorizontal, Search, UserX, RefreshCw, Copy, AlertTriangle, Check, Download, X, Ban, ShieldCheck, ShieldAlert, Bell, ChevronDown, Pencil, LayoutTemplate, Loader2, Crown, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import type { JSX } from 'react';
import { Modal, Spinner, Dropdown, DropdownItem, StatusBadge } from '@/components/shared/UIComponents';
import { fetchTenantUsers, changeUserRole, removeUser, blockUser, unblockUser, syncTenantAdminRole, updateUserEmail, setUserRight, setCreatorTier } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { errorMessage } from '@/lib/errorMessage';
import { guestDisplayName, shortId } from '@/lib/guestName';
import type { AppTenantUser } from '@/lib/api';
import type { Tenant, TenantPlan } from '@/types';
import { BuddyUserModerationModal, type BuddyUserModerationSummary } from '@/components/dashboard/BuddyUserModerationModal';
import { BuddyAuditLogCard } from '@/components/dashboard/BuddyAuditLogCard';
import { UserPlanHistory } from '@/components/tenants/UserPlanHistory';

const APP_SUPABASE_URL = import.meta.env.VITE_APP_SUPABASE_URL || '';
const PROVISION_SECRET = import.meta.env.VITE_PROVISION_SECRET || '';

export function UsersPage({ tenant }: { tenant: Tenant }) {
  const [search, setSearch] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [users, setUsers] = useState<AppTenantUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);

  // ── Change Role modal state ─────────────────────────────────────────
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [roleTarget, setRoleTarget] = useState<AppTenantUser | null>(null);
  const [newRole, setNewRole] = useState('member');
  const [roleLoading, setRoleLoading] = useState(false);
  const [roleError, setRoleError] = useState<string | null>(null);

  // ── Remove User modal state ─────────────────────────────────────────
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<AppTenantUser | null>(null);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  // ── Block / Unblock modal state ───────────────────────────────────
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockTarget, setBlockTarget] = useState<AppTenantUser | null>(null);
  const [blockLoading, setBlockLoading] = useState(false);
  const [blockError, setBlockError] = useState<string | null>(null);

  const [showUnblockModal, setShowUnblockModal] = useState(false);
  const [unblockTarget, setUnblockTarget] = useState<AppTenantUser | null>(null);
  const [unblockLoading, setUnblockLoading] = useState(false);
  const [unblockError, setUnblockError] = useState<string | null>(null);

  // ── Edit Email modal state ─────────────────────────────────────────
  const [showEmailModal,  setShowEmailModal]  = useState(false);
  const [emailTarget,     setEmailTarget]     = useState<AppTenantUser | null>(null);
  const [emailNewValue,   setEmailNewValue]   = useState('');
  const [emailLoading,    setEmailLoading]    = useState(false);
  const [emailError,      setEmailError]      = useState<string | null>(null);
  const [emailWarning,    setEmailWarning]    = useState<string | null>(null);
  const { user: actor }   = useAuth();

  // ── Sync warning (Platform ↔ App DB admin sync) ───────────────────
  const [syncWarning, setSyncWarning] = useState<string | null>(null);

  // ── Per-user Template right toggle ────────────────────────────────
  // Flips tenant_users.user_rights.itinerary_template in the App DB.
  // The App's ResultsScreen reads this on session boot and shows the
  // Template tab only when granted. rightBusyId drives the per-row
  // spinner so other rows stay clickable while one write is in flight.
  const [rightBusyId, setRightBusyId] = useState<string | null>(null);
  const toggleTemplateRight = async (u: AppTenantUser) => {
    const current = !!(u.user_rights?.itinerary_template);
    const next    = !current;
    setRightBusyId(u.user_id);
    try {
      const r = await setUserRight({
        tenantId:    tenant.id,
        userId:      u.user_id,
        rightKey:    'itinerary_template',
        value:       next,
        actorId:     actor?.id,
        actorEmail:  actor?.email ?? undefined,
      });
      // Splice the authoritative user_rights from the edge fn back
      // into our local list — covers the case where some other admin
      // flipped a different key on the same row in parallel.
      setUsers(prev => prev.map(row =>
        row.user_id === u.user_id
          ? { ...row, user_rights: r.user_rights }
          : row,
      ));
      toast.success(next
        ? `Template tab unlocked for ${u.email || u.user_id}`
        : `Template tab revoked for ${u.email || u.user_id}`);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update right');
    } finally {
      setRightBusyId(null);
    }
  };

  // ── Promote/Demote creator tier ──────────────────────────────────
  // Mirrors the super-admin TenantDetail flow. Tenant admins manage
  // their own creator roster, so they can pre-promote known
  // influencers AND demote bad actors without escalating to super-
  // admin. Reuses rightBusyId for the per-row spinner — only one
  // mutation per row is ever in flight (both buttons live in the same
  // Actions area), so a single busy id suffices.
  const toggleCreatorTier = async (u: AppTenantUser) => {
    const currentTier = (u.creator_tier ?? 'starter') as 'featured' | 'starter';
    const nextTier    = currentTier === 'featured' ? 'starter' : 'featured';
    setRightBusyId(u.user_id);
    try {
      const r = await setCreatorTier({
        tenantId:    tenant.id,
        userId:      u.user_id,
        tier:        nextTier,
        actorId:     actor?.id,
        actorEmail:  actor?.email ?? undefined,
      });
      setUsers(prev => prev.map(row =>
        row.user_id === u.user_id
          ? { ...row, creator_tier: r.creator_tier, creator_tier_promoted_at: r.creator_tier_promoted_at }
          : row,
      ));
      if (r.unchanged) {
        toast(`${u.email || u.user_id} is already ${nextTier}.`);
      } else if (nextTier === 'featured') {
        toast.success(`Promoted ${u.email || u.user_id} to Featured Creator`);
      } else {
        toast.success(`Demoted ${u.email || u.user_id} to Starter`);
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update tier');
    } finally {
      setRightBusyId(null);
    }
  };

  // ── Plan-history drill-in ─────────────────────────────────────────
  // Clicking a user row toggles an inline plan-timeline panel below it
  // (see UserPlanHistory). One row at a time so we don't fan out
  // PROVISION_SECRET fetches across the whole table.
  const [historyForUserId, setHistoryForUserId] = useState<string | null>(null);

  // ── Buddy Trips moderation state ──────────────────────────────────
  // Index keyed on user_id so each row in the users table can light up
  // a "Buddy" cell with the user's strike count / blocked / pending
  // unblock-request status. The full per-user detail (recent strikes,
  // top terms, unblock action) lives in BuddyUserModerationModal,
  // opened by clicking the Buddy chip.
  const [buddyByUser, setBuddyByUser] = useState<Map<string, BuddyUserModerationSummary>>(new Map());
  const [buddyModalUser, setBuddyModalUser] = useState<BuddyUserModerationSummary | null>(null);

  const plan = tenant.plan as TenantPlan | undefined;
  const maxUsers = tenant.max_users || plan?.max_users;
  const currentUsers = users.length || tenant.current_user_count || 0;

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchTenantUsers(tenant.id);
      setUsers(data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // Pull buddy moderation rows + pending requests for the tenant and
  // fold them into a single per-user index. Called on mount and after
  // any unblock action so the Buddy chip stays in sync.
  const loadBuddyModeration = useCallback(async () => {
    if (!APP_SUPABASE_URL || !PROVISION_SECRET) return;
    try {
      const headers = { 'Content-Type': 'application/json', 'x-provision-secret': PROVISION_SECRET } as const;
      const url = `${APP_SUPABASE_URL}/functions/v1/admin-buddy-analytics`;
      const [listRes, reqRes] = await Promise.all([
        fetch(url, { method: 'POST', headers, body: JSON.stringify({ mode: 'list_blocked', tenant_id: tenant.id }) }).then(r => r.json()),
        fetch(url, { method: 'POST', headers, body: JSON.stringify({ mode: 'list_unblock_requests', tenant_id: tenant.id }) }).then(r => r.json()),
      ]);
      const m = new Map<string, BuddyUserModerationSummary>();
      const pendingIds = new Set<string>();
      if (reqRes.ok) for (const r of reqRes.data ?? []) pendingIds.add(r.user_id);
      if (listRes.ok) {
        for (const row of listRes.data ?? []) {
          m.set(row.user_id, {
            user_id:             row.user_id,
            email:               row.email,
            full_name:           row.full_name,
            strike_count:        row.strike_count ?? 0,
            blocked:             Boolean(row.blocked),
            blocked_at:          row.blocked_at,
            last_strike_at:      row.last_strike_at,
            last_strike_terms:   row.last_strike_terms,
            last_strike_excerpt: row.last_strike_excerpt,
            pending_request:     pendingIds.has(row.user_id),
            times_blocked:       row.times_blocked ?? 0,
            times_unblocked:     row.times_unblocked ?? 0,
            first_blocked_at:    row.first_blocked_at ?? null,
            last_unblocked_at:   row.last_unblocked_at ?? null,
          });
        }
      }
      setBuddyByUser(m);
    } catch (err) {
      // Non-fatal — moderation chips just stay empty if the call fails.
      console.warn('[Users] buddy moderation load failed:', err);
    }
  }, [tenant.id]);

  useEffect(() => {
    loadUsers();
    loadBuddyModeration();
  }, [tenant.id, loadBuddyModeration]);

  // Counts for the optional in-page "X strikers / Y blocked" summary.
  const buddyCounts = useMemo(() => {
    let strikers = 0, blocked = 0, pending = 0;
    for (const v of buddyByUser.values()) {
      if (v.blocked) blocked += 1;
      else if (v.strike_count > 0) strikers += 1;
      if (v.pending_request) pending += 1;
    }
    return { strikers, blocked, pending };
  }, [buddyByUser]);

  // Hide guest (anonymous) users by default. They count toward tenant
  // totals but the admin UI gets noisy when every visitor who tapped
  // "Try the App" shows up. Admins can toggle them back on with the
  // "Show guests" button just above the user table.
  const [hideGuests, setHideGuests] = useState(true);
  const guestCount = useMemo(() => users.filter(u => u.is_anonymous).length, [users]);

  const filtered = users.filter(u =>
    (hideGuests ? !u.is_anonymous : true) && (
      !search ||
      (u.full_name || '').toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase())
    )
  );

  // ── Change Role handlers ────────────────────────────────────────────
  const openRoleModal = (user: AppTenantUser) => {
    setRoleTarget(user);
    setNewRole(user.role);
    setRoleError(null);
    setShowRoleModal(true);
  };

  const handleChangeRole = async () => {
    if (!roleTarget) return;
    setRoleLoading(true);
    setRoleError(null);
    try {
      const oldRole = roleTarget.role;
      await changeUserRole(tenant.id, roleTarget.user_id, newRole);
      setUsers(prev => prev.map(u => u.user_id === roleTarget.user_id ? { ...u, role: newRole } : u));
      setShowRoleModal(false);

      // Sync Platform DB admin role if changing to/from admin
      const wasAdmin = oldRole === 'admin';
      const isNowAdmin = newRole === 'admin';
      if (wasAdmin !== isNowAdmin && roleTarget.email) {
        try {
          await syncTenantAdminRole(tenant.id, roleTarget.email, isNowAdmin ? 'promote' : 'demote');
        } catch (syncErr: any) {
          setSyncWarning(`Role updated but Platform admin sync failed for ${roleTarget.email}: ${syncErr.message}. You may need to manually update the admin list.`);
        }
      }
    } catch (err: any) {
      setRoleError(err.message || 'Failed to change role');
    } finally {
      setRoleLoading(false);
    }
  };

  // ── Edit Email handlers ─────────────────────────────────────────────
  const openEmailModal = (user: AppTenantUser) => {
    setEmailTarget(user);
    setEmailNewValue(user.email || '');
    setEmailError(null);
    setEmailWarning(null);
    setShowEmailModal(true);
  };

  const handleSaveEmail = async () => {
    if (!emailTarget) return;
    const next = emailNewValue.trim().toLowerCase();
    if (!next || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(next)) {
      setEmailError('Enter a valid email address.');
      return;
    }
    if (next === (emailTarget.email || '').toLowerCase()) {
      setEmailError('That\'s the same as the current email.');
      return;
    }
    setEmailLoading(true);
    setEmailError(null);
    setEmailWarning(null);
    try {
      const result = await updateUserEmail({
        userId:     emailTarget.user_id,
        newEmail:   next,
        actorEmail: actor?.email,
      });
      // Patch the local users list so the row reflects the new email
      // without a full re-fetch.
      setUsers(prev => prev.map(u =>
        u.user_id === emailTarget.user_id ? { ...u, email: result.email } : u,
      ));
      if (result.warning) {
        // Partial success — keep the modal open with the warning so
        // the operator can take any follow-up action manually.
        setEmailWarning(result.warning);
      } else {
        setShowEmailModal(false);
      }
    } catch (err) {
      setEmailError(errorMessage(err));
    } finally {
      setEmailLoading(false);
    }
  };

  // ── Remove User handlers ────────────────────────────────────────────
  const openRemoveModal = (user: AppTenantUser) => {
    setRemoveTarget(user);
    setRemoveError(null);
    setShowRemoveModal(true);
  };

  const handleRemoveUser = async () => {
    if (!removeTarget) return;
    setRemoveLoading(true);
    setRemoveError(null);
    try {
      const result = await removeUser(tenant.id, removeTarget.user_id);
      setUsers(prev => prev.filter(u => u.user_id !== removeTarget.user_id));
      setShowRemoveModal(false);

      // If the removed user was an admin, demote from Platform DB
      if (removeTarget.role === 'admin' && removeTarget.email) {
        try {
          await syncTenantAdminRole(tenant.id, removeTarget.email, 'demote');
        } catch (syncErr: any) {
          setSyncWarning(`User removed but Platform admin sync failed for ${removeTarget.email}: ${syncErr.message}.`);
        }
      }

      // Tell the operator what actually happened to the auth record.
      // The default behavior is now "auto-delete auth user when this
      // was their last tenant" — make that visible so the operator
      // isn't surprised when the user disappears from the App DB too,
      // or reassured when they DON'T because the user is in other
      // tenants.
      if (result.authUserDeleted) {
        setSyncWarning(`${removeTarget.email || removeTarget.full_name || 'User'} was their last tenant — their account has been permanently deleted.`);
      } else if (result.remainingMemberships > 0) {
        setSyncWarning(`Removed from this tenant. The user is still a member of ${result.remainingMemberships} other tenant${result.remainingMemberships === 1 ? '' : 's'}, so their account is preserved.`);
      }
    } catch (err: any) {
      setRemoveError(err.message || 'Failed to remove user');
    } finally {
      setRemoveLoading(false);
    }
  };

  // ── Block User handlers ────────────────────────────────────────────
  const openBlockModal = (user: AppTenantUser) => {
    setBlockTarget(user);
    setBlockError(null);
    setShowBlockModal(true);
  };

  const handleBlockUser = async () => {
    if (!blockTarget) return;
    setBlockLoading(true);
    setBlockError(null);
    try {
      await blockUser(tenant.id, blockTarget.user_id);
      setUsers(prev => prev.map(u => u.user_id === blockTarget.user_id ? { ...u, status: 'blocked' } : u));
      setShowBlockModal(false);
    } catch (err: any) {
      setBlockError(err.message || 'Failed to block user');
    } finally {
      setBlockLoading(false);
    }
  };

  // ── Unblock User handlers ─────────────────────────────────────────
  const openUnblockModal = (user: AppTenantUser) => {
    setUnblockTarget(user);
    setUnblockError(null);
    setShowUnblockModal(true);
  };

  const handleUnblockUser = async () => {
    if (!unblockTarget) return;
    setUnblockLoading(true);
    setUnblockError(null);
    try {
      await unblockUser(tenant.id, unblockTarget.user_id);
      setUsers(prev => prev.map(u => u.user_id === unblockTarget.user_id ? { ...u, status: 'active' } : u));
      setShowUnblockModal(false);
    } catch (err: any) {
      setUnblockError(err.message || 'Failed to unblock user');
    } finally {
      setUnblockLoading(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(`https://${tenant.subdomain}`);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleExportUsers = () => {
    if (!users.length) return;
    const headers = ['Name', 'Email', 'Role', 'Plan', 'Status', 'Joined'];
    const rows = users.map(u => [
      u.full_name || u.email?.split('@')[0] || '',
      u.email || '',
      u.role || '',
      u.plan_key || '',
      u.status === 'blocked' ? 'Blocked' : (u.subscription_status || 'active'),
      u.joined_at ? new Date(u.joined_at).toLocaleDateString() : '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users-${tenant.subdomain || 'export'}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-surface-900">Users</h1>
          <p className="text-sm text-surface-500 mt-1">
            {currentUsers} of {maxUsers || '∞'} seats used
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleExportUsers} className="btn-secondary text-sm" title="Export CSV" disabled={!users.length}>
            <Download size={14} /> Export
          </button>
          <button onClick={loadUsers} className="btn-secondary text-sm" title="Refresh">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
          <button onClick={() => setShowInvite(true)} className="btn-primary text-sm">
            <UserPlus size={16} /> Invite User
          </button>
        </div>
      </div>

      {/* Seat usage bar */}
      {maxUsers && (
        <div className="glass-panel p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-surface-700">Seat Usage</span>
            <span className="text-sm font-medium text-surface-900">{currentUsers} / {maxUsers}</span>
          </div>
          <div className="h-2 bg-surface-200 rounded-full overflow-hidden">
            <div className="h-full bg-brand-500 rounded-full transition-all"
              style={{ width: `${Math.min(100, (currentUsers / maxUsers) * 100)}%` }} />
          </div>
          {currentUsers / maxUsers > 0.8 && (
            <p className="text-xs text-amber-400 mt-2">Approaching seat limit. Consider upgrading your plan.</p>
          )}
        </div>
      )}

      {/* Sync warning banner */}
      {syncWarning && (
        <div className="flex items-start gap-3 p-4 rounded-xl banner-amber border">
          <AlertTriangle size={18} className="text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-300 flex-1">{syncWarning}</p>
          <button onClick={() => setSyncWarning(null)} className="text-amber-400 hover:text-amber-300 flex-shrink-0">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Search + guest filter */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative w-72">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search users..." className="input-field pl-10 py-2" />
        </div>
        {/* Guest filter — only rendered when there are anon users to
            hide/reveal. Onboarded admins on a tenant with zero guests
            never see this control, so the UI stays uncluttered. */}
        {guestCount > 0 && (
          <button
            onClick={() => setHideGuests(g => !g)}
            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-surface-200 hover:bg-surface-100 transition-colors text-surface-700"
            title={hideGuests ? 'Show guest visitors (anonymous accounts)' : 'Hide guest visitors'}
          >
            {hideGuests
              ? <>Show {guestCount} guest{guestCount === 1 ? '' : 's'}</>
              : <>Hide {guestCount} guest{guestCount === 1 ? '' : 's'}</>}
          </button>
        )}
      </div>

      {/* Error state */}
      {error && (
        <div className="glass-panel p-6 text-center">
          <p className="text-sm text-red-600 dark:text-red-400 mb-3">{error}</p>
          <button onClick={loadUsers} className="btn-secondary text-sm">Retry</button>
        </div>
      )}

      {/* Loading state */}
      {loading && !error && (
        <div className="glass-panel p-12 flex items-center justify-center">
          <Spinner size="md" />
          <span className="ml-3 text-sm text-surface-500">Loading users...</span>
        </div>
      )}

      {/* Users table */}
      {!loading && !error && (
        <div className="glass-panel overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-surface-200">
                <th className="table-header-cell w-6"></th>
                <th className="table-header-cell">User</th>
                <th className="table-header-cell">Role</th>
                <th className="table-header-cell">Plan</th>
                <th className="table-header-cell">Status</th>
                <th className="table-header-cell">Buddy</th>
                <th className="table-header-cell">Joined</th>
                {/* Template right — controls the Template tab on the App
                    ResultsScreen ("Earn on every copy" creator-
                    monetization surface). Per-user grant on App DB
                    tenant_users.user_rights.itinerary_template. */}
                <th
                  className="table-header-cell"
                  title="Unlocks the Template tab on the App's itinerary view (earn on every copy)."
                >
                  Template
                </th>
                {/* Creator Program tier. Same Promote/Demote UX as the
                    super-admin TenantDetail page so tenant admins can
                    manage their own creator roster without escalating.
                    Auto-promote pg_cron handles the organic 50-sales /
                    500-followers path; this column is for manual
                    pre-promotion + policy demotion. */}
                <th
                  className="table-header-cell"
                  title="Featured Creator tier — boosted placement in public feeds + exclusive poster styles. Auto-promotes at 50 sales or 500 followers."
                >
                  Creator
                </th>
                <th className="table-header-cell w-10"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={10} className="px-5 py-12 text-center text-sm text-surface-500">
                  {users.length === 0 ? 'No users have joined this tenant yet.' : 'No users found matching your search.'}
                </td></tr>
              ) : filtered.map(user => {
                const isExpanded = historyForUserId === user.user_id;
                const toggle = () => setHistoryForUserId(isExpanded ? null : user.user_id);
                return (
                <Fragment key={user.user_id}>
                <tr
                  className="table-row cursor-pointer hover:bg-surface-50/60 dark:hover:bg-surface-100/30"
                  onClick={toggle}
                >
                  <td className="table-cell">
                    <ChevronDown
                      size={14}
                      className={`text-surface-400 transition-transform ${isExpanded ? 'rotate-0' : '-rotate-90'}`}
                    />
                  </td>
                  <td className="table-cell">
                    <div className="flex items-center gap-3">
                      {user.avatar_url ? (
                        <img src={user.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-brand-500/10 border border-brand-500/20 flex items-center justify-center text-brand-600 dark:text-brand-400 text-xs font-bold">
                          {(guestDisplayName(user) || user.email || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-surface-900">{guestDisplayName(user)}</div>
                        <div className="text-xs text-surface-500 font-mono" title={user.user_id}>
                          {user.email || (user.is_anonymous ? shortId(user.user_id) : '')}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="table-cell">
                    <div className="inline-flex items-center gap-1.5">
                      <RoleBadge role={user.role} />
                      {user.is_anonymous && (
                        <span
                          className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-700 border border-amber-200"
                          title="Anonymous account — hasn't added email yet"
                        >
                          Guest
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="table-cell">
                    <PlanBadge planKey={user.plan_key} planName={user.plan_name} />
                  </td>
                  <td className="table-cell">
                    {user.status === 'blocked' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium badge-red border border-red-800/40">
                        <Ban size={10} /> Blocked
                      </span>
                    ) : (
                      <StatusBadge status={user.subscription_status === 'active' ? 'active' : user.subscription_status === 'trialing' ? 'trial' : 'inactive'} />
                    )}
                  </td>
                  <td className="table-cell" onClick={(e) => e.stopPropagation()}>
                    <BuddyChip
                      summary={buddyByUser.get(user.user_id)}
                      onClick={() => {
                        const s = buddyByUser.get(user.user_id);
                        if (s) setBuddyModalUser(s);
                      }}
                    />
                  </td>
                  <td className="table-cell text-surface-500 text-xs">
                    {user.joined_at ? new Date(user.joined_at).toLocaleDateString() : '—'}
                  </td>
                  {/* Template right toggle — stops row click so the
                      plan-history drawer doesn't open. Writes via
                      set-user-right edge fn; optimistic local update on
                      success. */}
                  <td className="table-cell" onClick={(e) => e.stopPropagation()}>
                    {(() => {
                      const templateGranted = !!(user.user_rights?.itinerary_template);
                      const inFlight = rightBusyId === user.user_id;
                      return (
                        <button
                          onClick={() => { if (!inFlight) toggleTemplateRight(user); }}
                          disabled={inFlight}
                          title={templateGranted
                            ? 'Template tab unlocked. Click to revoke.'
                            : 'Template tab hidden. Click to grant.'}
                          className={[
                            'inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold transition-colors',
                            templateGranted
                              ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/25'
                              : 'bg-surface-100 text-surface-500 hover:bg-surface-200 dark:bg-surface-100/30 dark:text-surface-400 dark:hover:bg-surface-100/40',
                            inFlight ? 'opacity-60 cursor-wait' : '',
                          ].join(' ')}
                        >
                          {inFlight
                            ? <Loader2 size={11} className="animate-spin" />
                            : <LayoutTemplate size={11} />}
                          {templateGranted ? 'Granted' : 'Off'}
                        </button>
                      );
                    })()}
                  </td>
                  {/* Creator tier cell — Promote/Demote button + organic
                      counter sub-line. Stops row click so the plan-
                      history drawer doesn't open. Writes via set-creator-
                      tier edge fn; optimistic local update. */}
                  <td className="table-cell" onClick={(e) => e.stopPropagation()}>
                    {(() => {
                      const isFeatured = (user.creator_tier ?? 'starter') === 'featured';
                      const inFlight   = rightBusyId === user.user_id;
                      const sales      = Number(user.confirmed_blueprint_sales) || 0;
                      const followers  = Number(user.follower_count) || 0;
                      return (
                        <div className="flex flex-col items-start gap-0.5">
                          <button
                            onClick={() => { if (!inFlight) toggleCreatorTier(user); }}
                            disabled={inFlight}
                            title={isFeatured
                              ? 'Featured Creator — boosted in feeds + exclusive templates. Click to demote.'
                              : 'Promote to Featured Creator (boost in public feeds + exclusive poster styles). Click to grant.'}
                            className={[
                              'inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold transition-colors',
                              isFeatured
                                ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300 hover:bg-amber-500/25'
                                : 'bg-surface-100 text-surface-500 hover:bg-surface-200 dark:bg-surface-100/30 dark:text-surface-400 dark:hover:bg-surface-100/40',
                              inFlight ? 'opacity-60 cursor-wait' : '',
                            ].join(' ')}
                          >
                            {inFlight
                              ? <Loader2 size={11} className="animate-spin" />
                              : isFeatured ? <Crown size={11} /> : <Sparkles size={11} />}
                            {isFeatured ? 'Featured' : 'Promote'}
                          </button>
                          {/* Organic-promote progress — only show for
                              Starters with non-zero counters so Featured
                              rows stay tidy. */}
                          {!isFeatured && (sales > 0 || followers > 0) && (
                            <span className="text-[9px] text-surface-400 tabular-nums">
                              {sales}/50 · {followers}/500
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="table-cell" onClick={(e) => e.stopPropagation()}>
                    <Dropdown trigger={
                      <button className="p-1.5 rounded-lg hover:bg-surface-100 text-surface-400">
                        <MoreHorizontal size={14} />
                      </button>
                    }>
                      <DropdownItem onClick={() => openRoleModal(user)}><Shield size={14} className="inline mr-2" />Change Role</DropdownItem>
                      {!user.is_anonymous && (
                        <DropdownItem onClick={() => openEmailModal(user)}><Pencil size={14} className="inline mr-2" />Edit Email</DropdownItem>
                      )}
                      <DropdownItem onClick={() => window.open(`mailto:${user.email}`, '_blank')}><Mail size={14} className="inline mr-2" />Send Email</DropdownItem>
                      {user.status !== 'blocked' ? (
                        <DropdownItem onClick={() => openBlockModal(user)}><Ban size={14} className="inline mr-2" />Block User</DropdownItem>
                      ) : (
                        <DropdownItem onClick={() => openUnblockModal(user)}><ShieldCheck size={14} className="inline mr-2" />Unblock User</DropdownItem>
                      )}
                      <DropdownItem danger onClick={() => openRemoveModal(user)}><UserX size={14} className="inline mr-2" />Remove User</DropdownItem>
                    </Dropdown>
                  </td>
                </tr>
                {isExpanded && (
                  <tr className="border-b border-surface-200 bg-surface-50/40 dark:bg-surface-100/20">
                    <td colSpan={10} className="p-0">
                      <UserPlanHistory
                        tenantId={tenant.id}
                        userId={user.user_id}
                        userLabel={user.email || user.full_name || user.user_id}
                      />
                    </td>
                  </tr>
                )}
                </Fragment>
                );
              })}
            </tbody>
          </table>
          {filtered.length > 0 && (
            <p className="px-5 py-2 text-[11px] text-surface-500 border-t border-surface-200 bg-surface-50/30">
              Click a user row to view plan history.
            </p>
          )}
        </div>
      )}

      {/* ── Invite / Share Link modal ──────────────────────────────────── */}
      <Modal open={showInvite} onClose={() => setShowInvite(false)} title="Invite User" subtitle="Share this link for users to join your organisation">
        <div className="space-y-4">
          <div className="p-3 rounded-lg banner-blue border text-xs text-blue-400">
            Users can join by signing up at <strong>{tenant.subdomain}</strong>. Share the link below to invite them.
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs bg-surface-200 p-2 rounded font-mono truncate">https://{tenant.subdomain}</code>
            <button onClick={handleCopyLink} className="btn-secondary text-xs p-2" title="Copy link">
              {copiedLink ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
            </button>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => { setShowInvite(false); setInviteEmail(''); }} className="btn-secondary text-sm">Close</button>
          </div>
        </div>
      </Modal>

      {/* ── Change Role modal ──────────────────────────────────────────── */}
      <Modal open={showRoleModal} onClose={() => setShowRoleModal(false)} title="Change Role"
        subtitle={`Update the role for ${roleTarget?.full_name || roleTarget?.email || 'this user'}`}>
        <div className="space-y-4">
          <div className="p-3 bg-surface-50 border border-surface-200 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-brand-500/10 border border-brand-500/20 flex items-center justify-center text-brand-600 dark:text-brand-400 text-xs font-bold">
                {(roleTarget?.full_name || roleTarget?.email || '?')[0].toUpperCase()}
              </div>
              <div>
                <div className="text-sm font-medium text-surface-900">{roleTarget?.full_name || 'No name'}</div>
                <div className="text-xs text-surface-500">{roleTarget?.email}</div>
              </div>
            </div>
          </div>

          <div>
            <label className="input-label">New Role</label>
            <select className="input-field" value={newRole} onChange={e => setNewRole(e.target.value)}>
              <option value="admin">Admin — full access</option>
              <option value="member">Member — create & edit trips</option>
              <option value="viewer">Viewer — read only</option>
            </select>
          </div>

          {roleError && <div className="p-3 rounded-lg banner-red border text-sm text-red-600 dark:text-red-400">{roleError}</div>}

          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => setShowRoleModal(false)} className="btn-secondary text-sm" disabled={roleLoading}>Cancel</button>
            <button onClick={handleChangeRole} disabled={roleLoading || newRole === roleTarget?.role} className="btn-primary text-sm min-w-[140px] justify-center">
              {roleLoading ? <Spinner size="sm" /> : <><Shield size={14} /> Update Role</>}
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Edit Email modal ───────────────────────────────────────────
          Lets a Platform admin correct a typo'd registration email.
          Uses the admin API server-side so it bypasses the verification
          challenge — appropriate because an admin (not the user) is
          making the change, and the verification email would otherwise
          go to the OLD address the user can't access. */}
      <Modal open={showEmailModal} onClose={() => setShowEmailModal(false)} title="Edit Email"
        subtitle={`Correct a typo'd email for ${emailTarget?.full_name || emailTarget?.email || 'this user'}`}>
        <div className="space-y-4">
          <div className="p-3 bg-surface-50 border border-surface-200 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-brand-500/10 border border-brand-500/20 flex items-center justify-center text-brand-600 dark:text-brand-400 text-xs font-bold">
                {(emailTarget?.full_name || emailTarget?.email || '?')[0].toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium text-surface-900">{emailTarget?.full_name || 'No name'}</div>
                <div className="text-xs text-surface-500 truncate">Current: {emailTarget?.email || '—'}</div>
              </div>
            </div>
          </div>

          <div>
            <label className="input-label">New email</label>
            <input
              type="email"
              autoComplete="off"
              className="input-field"
              value={emailNewValue}
              onChange={(e) => { setEmailNewValue(e.target.value); setEmailError(null); }}
              placeholder="correct@example.com"
              disabled={emailLoading}
            />
            <p className="mt-1 text-[11px] text-surface-500">
              The user can sign in with the new email immediately, using their existing password.
              No verification email is sent — admin override.
            </p>
          </div>

          {emailError && (
            <div className="p-3 rounded-lg banner-red border text-sm text-red-600 dark:text-red-400">
              {emailError}
            </div>
          )}
          {emailWarning && (
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
              {emailWarning}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={() => setShowEmailModal(false)}
              className="btn-secondary text-sm"
              disabled={emailLoading}
            >
              Cancel
            </button>
            <button
              onClick={handleSaveEmail}
              disabled={emailLoading}
              className="btn-primary text-sm min-w-[140px] justify-center"
            >
              {emailLoading ? <Spinner size="sm" /> : <><Pencil size={14} /> Save email</>}
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Remove User modal ──────────────────────────────────────────── */}
      <Modal open={showRemoveModal} onClose={() => setShowRemoveModal(false)} title="Remove User"
        subtitle="This action cannot be undone">
        <div className="space-y-4">
          <div className="p-4 banner-red border rounded-xl">
            <div className="flex items-start gap-3">
              <AlertTriangle size={20} className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-red-800 font-medium">
                  Are you sure you want to remove {removeTarget?.full_name || removeTarget?.email}?
                </p>
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                  This permanently deletes their data on <strong>this tenant</strong> (trips, subscriptions, notifications, scanned docs) and revokes their access. If this is their <strong>only tenant</strong>, their account is fully deleted too. If they belong to other tenants, those memberships are preserved.
                </p>
              </div>
            </div>
          </div>

          {removeError && <div className="p-3 rounded-lg banner-red border text-sm text-red-600 dark:text-red-400">{removeError}</div>}

          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => setShowRemoveModal(false)} className="btn-secondary text-sm" disabled={removeLoading}>Cancel</button>
            <button onClick={handleRemoveUser} disabled={removeLoading}
              className="text-sm px-4 py-2 rounded-lg font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2 min-w-[160px] justify-center">
              {removeLoading ? <Spinner size="sm" /> : <><UserX size={14} /> Remove User</>}
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Block User modal ───────────────────────────────────────────── */}
      <Modal open={showBlockModal} onClose={() => setShowBlockModal(false)} title="Block User"
        subtitle="Block this user from accessing the app">
        <div className="space-y-4">
          <div className="p-4 banner-amber border rounded-xl">
            <div className="flex items-start gap-3">
              <Ban size={20} className="text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-amber-800 font-medium">
                  Block {blockTarget?.full_name || blockTarget?.email}?
                </p>
                <p className="text-xs text-amber-400 mt-1">
                  This will permanently revoke their access and force them out of the app. Their data will be preserved. They cannot re-register unless unblocked by an admin.
                </p>
              </div>
            </div>
          </div>

          {blockError && <div className="p-3 rounded-lg banner-red border text-sm text-red-600 dark:text-red-400">{blockError}</div>}

          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => setShowBlockModal(false)} className="btn-secondary text-sm" disabled={blockLoading}>Cancel</button>
            <button onClick={handleBlockUser} disabled={blockLoading}
              className="text-sm px-4 py-2 rounded-lg font-medium bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 inline-flex items-center gap-2 min-w-[140px] justify-center">
              {blockLoading ? <Spinner size="sm" /> : <><Ban size={14} /> Block User</>}
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Unblock User modal ─────────────────────────────────────────── */}
      <Modal open={showUnblockModal} onClose={() => setShowUnblockModal(false)} title="Unblock User"
        subtitle="Restore this user's access">
        <div className="space-y-4">
          <div className="p-4 banner-emerald border rounded-xl">
            <div className="flex items-start gap-3">
              <ShieldCheck size={20} className="text-emerald-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-emerald-800 font-medium">
                  Unblock {unblockTarget?.full_name || unblockTarget?.email}?
                </p>
                <p className="text-xs text-emerald-400 mt-1">
                  This will restore their access to the app. All their previous data will be available again.
                </p>
              </div>
            </div>
          </div>

          {unblockError && <div className="p-3 rounded-lg banner-red border text-sm text-red-600 dark:text-red-400">{unblockError}</div>}

          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => setShowUnblockModal(false)} className="btn-secondary text-sm" disabled={unblockLoading}>Cancel</button>
            <button onClick={handleUnblockUser} disabled={unblockLoading}
              className="text-sm px-4 py-2 rounded-lg font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-2 min-w-[160px] justify-center">
              {unblockLoading ? <Spinner size="sm" /> : <><ShieldCheck size={14} /> Unblock User</>}
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Buddy Trips per-user moderation modal ────────────────────── */}
      <BuddyUserModerationModal
        open={buddyModalUser !== null}
        onClose={() => setBuddyModalUser(null)}
        tenantId={tenant.id}
        user={buddyModalUser}
        onChanged={() => loadBuddyModeration()}
      />

      {/* ── Buddy Trips audit log (legal-grade) — sits below the user
          table so admins can scan all moderation events for this tenant
          and export to CSV / PDF. Hidden when there's no history. */}
      <div className="mt-6">
        <BuddyAuditLogCard tenantId={tenant.id} tenantName={tenant.name} />
      </div>
    </div>
  );
}

// ── Buddy chip — small per-row indicator for the Users table.
//
// Status pill sets the dominant tone (blocked / striking / history /
// empty). When the user has any block history, a secondary counter
// chip pinned next to it shows the lifetime split:
//
//      [Blocked]  🔴2 / 🟢1
//      [3 strikes]  🔴1 / 🟢1
//      [History]   🔴2 / 🟢2
//
// Pure red number = blocks performed. Pure green number = unblocks
// performed. The chip is clickable as a single button — opens the
// per-user modal with the full timeline.
function BuddyChip({
  summary,
  onClick,
}: {
  summary?: BuddyUserModerationSummary;
  onClick: () => void;
}) {
  if (!summary) return <span className="text-xs text-surface-400">—</span>;

  const isBlocked    = summary.blocked;
  const isStriking   = !isBlocked && summary.strike_count > 0;
  const blocks       = summary.times_blocked   ?? 0;
  const unblocks     = summary.times_unblocked ?? 0;
  const hasHistory   = blocks > 0 || Boolean(summary.last_strike_at);
  const hasAnyState  = isBlocked || isStriking || hasHistory;

  if (!hasAnyState) {
    return <span className="text-xs text-surface-400">—</span>;
  }

  // Status pill
  let stateTone: string;
  let stateIcon: JSX.Element;
  let stateLabel: string;
  if (isBlocked) {
    stateTone  = 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700/50';
    stateIcon  = <Ban size={11} />;
    stateLabel = 'Blocked';
  } else if (isStriking) {
    stateTone  = 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700/50';
    stateIcon  = <ShieldAlert size={11} />;
    stateLabel = `${summary.strike_count} strike${summary.strike_count === 1 ? '' : 's'}`;
  } else {
    stateTone  = 'bg-surface-100 dark:bg-surface-200/30 text-surface-600 dark:text-surface-500 border-surface-300';
    stateIcon  = <ShieldCheck size={11} />;
    stateLabel = 'History';
  }

  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1.5 hover:opacity-90 transition-opacity"
      title={`B:${blocks} blocked · U:${unblocks} unblocked${summary.pending_request ? ' · pending unblock request' : ''}`}
    >
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${stateTone}`}>
        {stateIcon}
        {stateLabel}
        {summary.pending_request && (
          <span className="inline-flex items-center gap-0.5 ml-0.5 pl-1.5 border-l border-current/30">
            <Bell size={10} />
          </span>
        )}
      </span>
      {(blocks > 0 || unblocks > 0) && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-surface-300 bg-surface-50 dark:bg-surface-100/30 text-[10px] tabular-nums">
          <span className="inline-flex items-center gap-0.5 text-red-700 dark:text-red-400">
            <Ban size={9} />
            <span className="font-bold">{blocks}</span>
          </span>
          <span className="text-surface-300">/</span>
          <span className="inline-flex items-center gap-0.5 text-emerald-700 dark:text-emerald-400">
            <ShieldCheck size={9} />
            <span className="font-bold">{unblocks}</span>
          </span>
        </span>
      )}
    </button>
  );
}

function RoleBadge({ role }: { role: string }) {
  const styles: Record<string, string> = {
    admin: 'badge-violet border-violet-800/40',
    member: 'badge-blue border-blue-800/40',
    viewer: 'bg-surface-100 text-surface-600 border-surface-300',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${styles[role] || styles.member}`}>
      {role.charAt(0).toUpperCase() + role.slice(1)}
    </span>
  );
}

function PlanBadge({ planKey, planName }: { planKey: string; planName: string }) {
  const styles: Record<string, string> = {
    free: 'bg-surface-100 text-surface-600',
    explorer: 'badge-blue',
    adventurer: 'badge-teal',
    navigator: 'badge-amber',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${styles[planKey] || styles.free}`}>
      {planName || planKey || 'None'}
    </span>
  );
}
